import { useEffect, useState } from 'react';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import {
  GroupEntityTagsContext,
  GroupEntityTagsDispatchContext,
  GroupEntityTagsFetchContext,
  GroupEntityTagsLoadingContext,
  GroupEntityTagsUpdateContext,
} from './context';
import type { GroupTagsContextValue, GroupTagUpdateItem } from './interfaces';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Props = IWorkspaceFolderId & ChildrenProps;

export default function GroupFolderTagsContainer({ children, folderId }: Props) {
  const [state, dispatch] = useState<GroupTagsContextValue>(GroupFolderTagsContainer.initialState);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: ['get:group/tags/folder', folderId],
    queryFn: () => {
      return apiClient.tags.getFolderTags({ folderId });
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isSuccess && query.data?.tags) {
      dispatch(query.data.tags);
    }
  }, [query.data?.tags, query.isSuccess]);

  const { mutateAsync } = useMutation({
    mutationFn: (tags: GroupTagUpdateItem[]) => {
      return apiClient.tags.updateFolderTags({
        folderId,
        tags,
      });
    },
    onSuccess: result => {
      query.refetch();
    },
  });

  return (
    <GroupEntityTagsContext.Provider value={state}>
      <GroupEntityTagsLoadingContext.Provider value={query.isLoading}>
        <GroupEntityTagsFetchContext.Provider value={query.refetch}>
          <GroupEntityTagsUpdateContext.Provider value={mutateAsync}>
            <GroupEntityTagsDispatchContext.Provider value={dispatch}>
              {children}
            </GroupEntityTagsDispatchContext.Provider>
          </GroupEntityTagsUpdateContext.Provider>
        </GroupEntityTagsFetchContext.Provider>
      </GroupEntityTagsLoadingContext.Provider>
    </GroupEntityTagsContext.Provider>
  );
}

const initial: GroupTagsContextValue = [];

GroupFolderTagsContainer.initialState = initial;

export { GroupFolderTagsContainer };